import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { TrendingUp, AlertTriangle, DollarSign, Clock, CheckCircle2, Target, Brain, Filter, X } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import KPICard from '../components/KPICard';
import Timeline from '../components/Timeline';
import { api } from '../api/client';
import { useFilters } from '../context/FilterContext';
import { useAuth } from '../context/AuthContext';
import { useProductMode } from '../context/ProductModeContext';
import QuickScopeModal from '../components/QuickScopeModal';
import CheckpointSummaryPanel from '../components/CheckpointSummaryPanel';
import ProactiveSuggestionsBanner from '../components/ProactiveSuggestionsBanner';
import { ProjectResourceSection } from '../components/ResourceDashboardWidgets';
import { ProjectResourceRollup } from '../components/ProjectResourceRollup';
import ProjectBenefitsPanel from '../components/ProjectBenefitsPanel';

// ==========================================
// INLINE PROJECT FILTER BAR
// ==========================================
const ProjectFilterBar = ({ projectId, onFilterChange, activeFilters = {} }) => {
  const [filterOptions, setFilterOptions] = useState({
    statuses: [],
    owners: [],
    types: [],
  });
  const [loading, setLoading] = useState(true);
  const [localFilters, setLocalFilters] = useState(activeFilters);

  useEffect(() => {
    loadFilterOptions();
  }, [projectId]);

  useEffect(() => {
    setLocalFilters(activeFilters);
  }, [activeFilters]);

  const loadFilterOptions = async () => {
    setLoading(true);
    try {
      // Get filter options from API
      const response = await api.getFilters();
      const data = response.data || {};
      
      // Extract team names from objects - teams can be [{id, name}] or strings
      const teamNames = (data.teams || []).map(t => typeof t === 'object' ? t.name : t);
      setFilterOptions({
        statuses: data.statuses || [
          { status: 'Not Started' },
          { status: 'In Progress' },
          { status: 'Completed' },
          { status: 'Blocked' },
          { status: 'On Hold' },
        ],
        owners: teamNames,
        types: ['Feature', 'Story', 'Task', 'Bug', 'Epic'],
      });
    } catch (err) {
      console.error('Failed to load filter options:', err);
      // Fallback options
      setFilterOptions({
        statuses: [
          { status: 'Not Started' },
          { status: 'In Progress' },
          { status: 'Completed' },
          { status: 'Blocked' },
        ],
        owners: [],
        types: ['Feature', 'Story', 'Task', 'Bug'],
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...localFilters, [key]: value || undefined };
    // Remove undefined/empty values
    Object.keys(newFilters).forEach(k => {
      if (!newFilters[k]) delete newFilters[k];
    });
    setLocalFilters(newFilters);
    onFilterChange(newFilters);
  };

  const clearAllFilters = () => {
    setLocalFilters({});
    onFilterChange({});
  };

  const hasActiveFilters = Object.keys(localFilters).length > 0;

  return (
    <div className="bg-[var(--clr-surface-card)] rounded-xl border border-gray-200 p-4 mb-6 shadow-[var(--shadow-card)]" data-testid="project-filter-bar">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <Filter className="w-5 h-5 text-[var(--clr-text-secondary)]" />
          <h3 className="text-sm font-semibold text-[var(--clr-text-primary)]">Filters</h3>
          {hasActiveFilters && (
            <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">
              {Object.keys(localFilters).length} active
            </span>
          )}
        </div>
        {hasActiveFilters && (
          <button
            onClick={clearAllFilters}
            className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 transition-colors"
            data-testid="clear-filters-btn"
          >
            <X className="w-3.5 h-3.5" />
            Clear all
          </button>
        )}
      </div>
      
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {/* Status Filter */}
        <div>
          <label className="block text-xs text-gray-500 mb-1">Status</label>
          <select
            value={localFilters.status || ''}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--clr-brand-primary)] bg-white"
            data-testid="filter-status"
            disabled={loading}
          >
            <option value="">All Statuses</option>
            {filterOptions.statuses.map((s) => (
              <option key={s.status} value={s.status}>{s.status}</option>
            ))}
          </select>
        </div>

        {/* Owner/Team Filter */}
        <div>
          <label className="block text-xs text-gray-500 mb-1">Team</label>
          <select
            value={localFilters.team || ''}
            onChange={(e) => handleFilterChange('team', e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--clr-brand-primary)] bg-white"
            data-testid="filter-team"
            disabled={loading}
          >
            <option value="">All Teams</option>
            {filterOptions.owners.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>

        {/* Type Filter */}
        <div>
          <label className="block text-xs text-gray-500 mb-1">Type</label>
          <select
            value={localFilters.type || ''}
            onChange={(e) => handleFilterChange('type', e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--clr-brand-primary)] bg-white"
            data-testid="filter-type"
            disabled={loading}
          >
            <option value="">All Types</option>
            {filterOptions.types.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>

        {/* Date Range Indicator */}
        <div>
          <label className="block text-xs text-gray-500 mb-1">Date Range</label>
          <select
            value={localFilters.date_range || ''}
            onChange={(e) => handleFilterChange('date_range', e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--clr-brand-primary)] bg-white"
            data-testid="filter-date-range"
            disabled={loading}
          >
            <option value="">All Time</option>
            <option value="this_week">This Week</option>
            <option value="this_month">This Month</option>
            <option value="this_quarter">This Quarter</option>
            <option value="overdue">Overdue Only</option>
          </select>
        </div>
      </div>
    </div>
  );
};

const ProjectDashboard = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { filters, updateFilter, clearFilters, getFilterParams, hasActiveFilters } = useFilters();
  const { user } = useAuth();
  const { getFeature } = useProductMode();
  
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState('features');
  const [showQuickScope, setShowQuickScope] = useState(false);
  const [scopeCapabilities, setScopeCapabilities] = useState(null);
  const [hasBaseline, setHasBaseline] = useState(false);
  const [aipmState, setAipmState] = useState(null);
  const [localFilters, setLocalFilters] = useState({});

  const tenantId = user?.tenant?.id || user?.tenant_id;
  
  // Check if AI features are enabled
  const hasCheckpoints = getFeature('ai.checkpoints', false);
  const hasProactiveSuggestions = getFeature('ai.proactiveSuggestions', false);

  useEffect(() => {
    loadDashboard();
    loadScopeInfo();
    loadAIPMState();
  }, [projectId, filters, tenantId, localFilters]);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const filterParams = { ...getFilterParams(), ...localFilters };
      const response = await api.getProjectDashboard(projectId, filterParams);
      setData(response.data);
    } catch (error) {
      console.error('Failed to load project dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilters) => {
    setLocalFilters(newFilters);
  };

  const loadScopeInfo = async () => {
    if (!tenantId) return;
    try {
      // Check scoping capabilities
      const capRes = await api.getScopingCapabilities(tenantId);
      setScopeCapabilities(capRes.data.capabilities);

      // Check if project has approved baselines
      if (capRes.data.capabilities?.enabled) {
        const baselinesRes = await api.listBaselines({
          tenant_id: tenantId,
          project_id: projectId
        });
        setHasBaseline((baselinesRes.data.baselines || []).length > 0);
      }
    } catch (error) {
      console.error('Failed to load scope info:', error);
    }
  };

  const loadAIPMState = async () => {
    if (!tenantId || !projectId) return;
    try {
      const res = await api.getAIPMState(projectId, tenantId);
      setAipmState(res.data);
    } catch (error) {
      // AI PM may not be available for this tenant/project - that's ok
      console.debug('AI PM state not available:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--clr-brand-primary)]"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-[var(--clr-text-muted)]">Failed to load dashboard data</p>
      </div>
    );
  }

  const { project, kpis, features = [], stories = [], tasks = [], issues, decisions } = data;

  return (
    <div className="min-h-screen bg-[var(--clr-surface-subtle)]">
      <div className="max-w-[1380px] mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="text-[var(--clr-brand-primary)] hover:text-blue-700 text-sm font-medium mb-2"
            data-testid="back-button"
          >
            ← Back
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-[var(--clr-text-primary)] mb-2">{project.name}</h1>
              <p className="text-[var(--clr-text-secondary)]">Project Dashboard</p>
            </div>
            <div className="flex items-center gap-3">
              {/* Agentic PM Button - Show if tier allows */}
              {aipmState?.tier_access?.allowed && (
                <Link
                  to={`/project/${projectId}/ai-pm`}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-opacity shadow-md ${
                    aipmState?.enabled 
                      ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white hover:opacity-90'
                      : 'bg-white border border-purple-200 text-purple-700 hover:bg-purple-50'
                  }`}
                  data-testid="aipm-dashboard-btn"
                >
                  <Brain className="w-4 h-4" />
                  Agentic PM
                  {aipmState?.enabled && (
                    <span className="px-1.5 py-0.5 text-[10px] bg-white/20 rounded-full">Active</span>
                  )}
                </Link>
              )}
              {/* Quick Scope Button */}
              {scopeCapabilities?.enabled && (
                <button
                  onClick={() => setShowQuickScope(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-lg hover:opacity-90 transition-opacity shadow-md"
                  data-testid="quick-scope-btn"
                >
                  <Target className="w-4 h-4" />
                  Quick Scope
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Filter Bar */}
        <ProjectFilterBar
          projectId={projectId}
          onFilterChange={handleFilterChange}
          activeFilters={localFilters}
        />

        {/* KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          <KPICard
            data-testid="stories-complete-kpi"
            title="Stories Complete"
            value={kpis?.stories_complete ?? 0}
            icon={<CheckCircle2 className="w-8 h-8 text-[var(--clr-sev-healthy)]" />}
          />
          <KPICard
            data-testid="percent-complete-kpi"
            title="% Complete"
            value={`${kpis?.percent_complete ?? 0}%`}
            icon={<TrendingUp className="w-8 h-8 text-[var(--clr-brand-primary)]" />}
          />
          <KPICard
            data-testid="overdue-kpi"
            title="Overdue"
            value={kpis?.overdue_count ?? 0}
            subtitle="Items overdue"
            icon={<Clock className="w-8 h-8 text-[var(--clr-sev-blocked)]" />}
          />
          <KPICard
            data-testid="velocity-kpi"
            title="Velocity"
            value={kpis?.velocity ?? 0}
            subtitle="Hours completed"
            icon={<TrendingUp className="w-8 h-8 text-purple-600" />}
          />
          <KPICard
            data-testid="cost-variance-kpi"
            title="Cost Variance"
            value={`$${((kpis?.cost_metrics?.variance ?? 0) / 1000).toFixed(1)}K`}
            subtitle={`${(kpis?.cost_metrics?.variance_pct ?? 0) > 0 ? '+' : ''}${kpis?.cost_metrics?.variance_pct ?? 0}%`}
            icon={<DollarSign className="w-8 h-8 text-purple-600" />}
          />
        </div>

        {/* Cost Summary */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <KPICard
            data-testid="estimated-cost-kpi"
            title="Estimated Cost"
            value={`$${((kpis?.cost_metrics?.est_cost ?? 0) / 1000).toFixed(1)}K`}
            icon={<DollarSign className="w-8 h-8 text-[var(--clr-brand-primary)]" />}
          />
          <KPICard
            data-testid="actual-cost-kpi"
            title="Actual Cost"
            value={`$${((kpis?.cost_metrics?.act_cost ?? 0) / 1000).toFixed(1)}K`}
            icon={<DollarSign className="w-8 h-8 text-purple-600" />}
          />
        </div>
        
        {/* Proactive Suggestions Banner */}
        {hasProactiveSuggestions && (
          <ProactiveSuggestionsBanner
            context="project_dashboard"
            entityId={projectId}
            entityType="project"
            className="mb-6"
          />
        )}
        
        {/* Checkpoint Summary Panel */}
        {hasCheckpoints && (
          <CheckpointSummaryPanel
            projectId={projectId}
            className="mb-8"
            variant="full"
          />
        )}

        {/* Tabs for Features / Stories / Tasks */}
        <div className="bg-[var(--clr-surface-card)] rounded-xl border border-gray-200 shadow-[var(--shadow-card)] mb-8">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <div className="border-b border-gray-200 px-6 pt-6">
              <TabsList className="bg-[var(--clr-surface-subtle)] p-1 rounded-lg">
                <TabsTrigger 
                  value="features" 
                  className="px-6 py-2 data-[state=active]:bg-white data-[state=active]:text-[var(--clr-brand-primary)] data-[state=active]:shadow-[var(--shadow-card)] rounded-md transition-all"
                  data-testid="features-tab"
                >
                  Features ({features.length})
                </TabsTrigger>
                <TabsTrigger 
                  value="stories" 
                  className="px-6 py-2 data-[state=active]:bg-white data-[state=active]:text-[var(--clr-brand-primary)] data-[state=active]:shadow-[var(--shadow-card)] rounded-md transition-all"
                  data-testid="stories-tab"
                >
                  Stories ({stories.length})
                </TabsTrigger>
                <TabsTrigger 
                  value="tasks" 
                  className="px-6 py-2 data-[state=active]:bg-white data-[state=active]:text-[var(--clr-brand-primary)] data-[state=active]:shadow-[var(--shadow-card)] rounded-md transition-all"
                  data-testid="tasks-tab"
                >
                  Tasks ({tasks.length})
                </TabsTrigger>
                <TabsTrigger 
                  value="benefits" 
                  className="px-6 py-2 data-[state=active]:bg-white data-[state=active]:text-emerald-600 data-[state=active]:shadow-[var(--shadow-card)] rounded-md transition-all"
                  data-testid="benefits-tab"
                >
                  <Target className="w-4 h-4 mr-1.5 inline" />
                  Benefits
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="features" className="p-6">
              {features.length > 0 ? (
                <Timeline data={features} />
              ) : (
                <p className="text-[var(--clr-text-muted)] text-center py-8">No features available</p>
              )}
            </TabsContent>

            <TabsContent value="stories" className="p-6">
              {stories.length > 0 ? (
                <Timeline data={stories} />
              ) : (
                <p className="text-[var(--clr-text-muted)] text-center py-8">No stories available</p>
              )}
            </TabsContent>

            <TabsContent value="tasks" className="p-6">
              {tasks.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-[var(--clr-surface-subtle)]">
                      <tr>
                        <th className="px-4 py-3 text-left font-medium text-gray-700">Task</th>
                        <th className="px-4 py-3 text-left font-medium text-gray-700">Team</th>
                        <th className="px-4 py-3 text-left font-medium text-gray-700">Responsible</th>
                        <th className="px-4 py-3 text-left font-medium text-gray-700">Status</th>
                        <th className="px-4 py-3 text-left font-medium text-gray-700">Due Date</th>
                        <th className="px-4 py-3 text-left font-medium text-gray-700">Hours</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {tasks.map((task, idx) => (
                        <tr key={idx} className="hover:bg-[var(--clr-surface-hover)]" data-testid={`task-${idx}`}>
                          <td className="px-4 py-3 text-[var(--clr-text-primary)]">{task.task || 'Unnamed Task'}</td>
                          <td className="px-4 py-3 text-[var(--clr-text-secondary)]">{task.team || '-'}</td>
                          <td className="px-4 py-3 text-[var(--clr-text-secondary)]">{task.responsible || '-'}</td>
                          <td className="px-4 py-3">
                            <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                              task.status === 'Completed' ? 'bg-green-100 text-green-700' :
                              task.status === 'In Progress' ? 'bg-blue-100 text-blue-700' :
                              task.status === 'Blocked' ? 'bg-red-100 text-red-700' :
                              'bg-[var(--clr-surface-subtle)] text-gray-700'
                            }`}>
                              {task.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-[var(--clr-text-secondary)]">
                            {task.due_date ? new Date(task.due_date).toLocaleDateString() : '-'}
                          </td>
                          <td className="px-4 py-3 text-[var(--clr-text-secondary)]">
                            {task.hours_act || 0} / {task.hours_est || 0}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-[var(--clr-text-muted)] text-center py-8">No tasks available</p>
              )}
            </TabsContent>
            
            <TabsContent value="benefits" className="p-6">
              <ProjectBenefitsPanel 
                projectId={projectId}
                onBenefitClick={(benefit) => {
                  console.log('View benefit:', benefit.benefit_id);
                }}
              />
            </TabsContent>
          </Tabs>
        </div>

        {/* Issues & Decisions */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Issues */}
          <div className="bg-[var(--clr-surface-card)] rounded-xl border border-gray-200 p-6 shadow-[var(--shadow-card)]">
            <h2 className="text-xl font-semibold text-[var(--clr-text-primary)] mb-4" data-testid="issues-title">Issues</h2>
            <div className="space-y-3">
              {issues && issues.length > 0 ? (
                issues.map((issue) => (
                  <div 
                    key={issue.id}
                    data-testid={`issue-${issue.issue_key}`}
                    className="p-4 bg-[var(--clr-surface-subtle)] rounded-lg hover:bg-[var(--clr-surface-hover)] transition-colors"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <span className="text-sm font-medium text-[var(--clr-text-primary)]">{issue.issue_key}</span>
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                        issue.risk_score >= 7 ? 'bg-red-100 text-red-700' :
                        issue.risk_score >= 4 ? 'bg-yellow-100 text-yellow-700' :
                        'bg-green-100 text-green-700'
                      }`}>
                        Risk: {issue.risk_score}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700 mb-2">{issue.title}</p>
                    <div className="flex items-center gap-4 text-xs text-[var(--clr-text-muted)]">
                      <span>{issue.type}</span>
                      <span>•</span>
                      <span>{issue.priority} Priority</span>
                      <span>•</span>
                      <span>{issue.status}</span>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-[var(--clr-text-muted)] text-center py-4">No issues reported</p>
              )}
            </div>
          </div>

          {/* Decisions */}
          <div className="bg-[var(--clr-surface-card)] rounded-xl border border-gray-200 p-6 shadow-[var(--shadow-card)]">
            <h2 className="text-xl font-semibold text-[var(--clr-text-primary)] mb-4" data-testid="decisions-title">Decisions</h2>
            <div className="space-y-3">
              {decisions && decisions.length > 0 ? (
                decisions.map((decision) => (
                  <div 
                    key={decision.id}
                    data-testid={`decision-${decision.id}`}
                    className="p-4 bg-[var(--clr-surface-subtle)] rounded-lg hover:bg-[var(--clr-surface-hover)] transition-colors"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <p className="text-sm font-medium text-[var(--clr-text-primary)]">{decision.title}</p>
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                        decision.status === 'Approved' ? 'bg-green-100 text-green-700' :
                        decision.status === 'Rejected' ? 'bg-red-100 text-red-700' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>
                        {decision.status}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-[var(--clr-text-muted)]">
                      <span>{decision.request_type}</span>
                      {decision.request_date && (
                        <>
                          <span>•</span>
                          <span>Requested: {new Date(decision.request_date).toLocaleDateString()}</span>
                        </>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-[var(--clr-text-muted)] text-center py-4">No decisions recorded</p>
              )}
            </div>
          </div>
        </div>

        {/* P6: Resource Management Section */}
        {tenantId && (
          <div className="mt-8">
            <ProjectResourceSection 
              tenantId={tenantId}
              projectId={projectId}
              onNavigate={navigate}
            />
          </div>
        )}

        {/* P7: Resource Demand vs Capacity Rollup */}
        {tenantId && (
          <div className="mt-8">
            <ProjectResourceRollup
              projectId={projectId}
              tenantId={tenantId}
              onNavigate={navigate}
            />
          </div>
        )}
      </div>

      {/* Quick Scope Modal */}
      <QuickScopeModal
        isOpen={showQuickScope}
        onClose={() => setShowQuickScope(false)}
        projectId={projectId}
        projectName={project?.name}
        hasExistingBaseline={hasBaseline}
      />
    </div>
  );
};

export default ProjectDashboard;
